import { useAuth, useClerk, useUser } from "@clerk/clerk-react";
import {
  Close,
  ExpandMore,
  ExploreOutlined,
  LogoutOutlined,
  ManageAccountsOutlined,
  PersonOutline,
} from "@mui/icons-material";
import {
  Collapse,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import UserAvatar from "@/components/users/UserAvatar";
import { TOUR_COPY, TOUR_TARGET_IDS } from "@/constants/tour";
import { useTour } from "@/context/TourContext";
import { useOrganization } from "@/hooks/useOrganizations";
import { usePermissions } from "@/hooks/usePermissions";
import { isTourEligible } from "@/utils/tour-helper";

interface DrawerUserProfileProps {
  isDrawerOpen: boolean;
  isMobile: boolean;
  footerExpanded: boolean;
  toggleFooterMenu: () => void;
  handleNavigation: (path: string) => void;
  isActivePath: (path: string) => boolean;
}

export function DrawerUserProfile({
  isDrawerOpen,
  isMobile,
  footerExpanded,
  toggleFooterMenu,
  handleNavigation,
  isActivePath,
}: DrawerUserProfileProps) {
  const { signOut } = useClerk();
  const { user, isLoaded } = useUser();
  const { actor } = useAuth();
  const { can, organizationId, isGfg, userId, isLoaded: arePermissionsLoaded } = usePermissions();
  const { data: org } = useOrganization(organizationId);
  const { start } = useTour();

  const canTakeTour = isTourEligible({
    isLoaded: arePermissionsLoaded,
    isGfg,
    isImpersonating: !!actor,
    isMobile,
    canEditCompany: can("edit", "COMPANY"),
  });

  const handleTakeTour = () => {
    if (footerExpanded) toggleFooterMenu();
    start();
  };

  return (
    <>
      <ListItemButton
        id={TOUR_TARGET_IDS.sidebarUser}
        onClick={toggleFooterMenu}
        // Collapsed, the avatar (alt="") is the button's only child, so it needs its own name.
        // Expanded, the user's name is already visible text right next to it — omit aria-label
        // there so the accessible name comes from that text instead of duplicating it.
        aria-label={isDrawerOpen ? undefined : "Account menu"}
        className={`px-4 py-3 flex items-center ${isDrawerOpen ? "justify-start" : "justify-center"}`}
      >
        <UserAvatar
          imageUrl={user?.imageUrl}
          hasImage={user?.hasImage}
          seed={userId}
          size={36}
          alt=""
          className="border-2 border-rodeo-dust"
        />
        {isDrawerOpen && (
          <>
            <div className="flex-1 ml-3 min-w-0 flex flex-col">
              {isLoaded && user ? (
                <>
                  <Typography
                    className="text-sm font-semibold truncate text-gray-900"
                    sx={{ fontSize: "14px", fontWeight: 600 }}
                  >
                    {user.firstName} {user.lastName}
                  </Typography>
                  <Typography
                    className="text-xs text-gray-500 truncate"
                    sx={{ fontSize: "12px", color: "var(--color-graphite)" }}
                  >
                    {org?.name ?? "No organization"}
                  </Typography>
                </>
              ) : (
                <>
                  <Typography className="text-sm font-semibold text-gray-900">
                    Loading...
                  </Typography>
                  <Typography className="text-xs text-gray-500">...</Typography>
                </>
              )}
            </div>
            {footerExpanded ? (
              <Close className="text-gray-500 ml-2" fontSize="small" />
            ) : (
              <ExpandMore className="text-gray-500 ml-2" />
            )}
          </>
        )}
      </ListItemButton>
      <Divider className="border-gray-200" />

      <Collapse in={footerExpanded} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          <ListItem disablePadding className={isMobile ? "my-0" : ""}>
            <ListItemButton
              onClick={() => handleNavigation("/account")}
              className={`min-h-[44px] ${isActivePath("/account") ? "bg-smoke" : ""} ${isDrawerOpen ? "px-6" : "justify-center px-2"}`}
            >
              <ListItemIcon
                className={`min-w-0 ${isActivePath("/account") ? "text-gray-900" : "text-gray-600"} ${isDrawerOpen ? "mr-4" : "mr-0"}`}
              >
                <ManageAccountsOutlined fontSize="small" />
              </ListItemIcon>
              {isDrawerOpen && (
                <ListItemText
                  primary="Account"
                  className={isActivePath("/account") ? "text-gray-900" : "text-gray-700"}
                  slotProps={{
                    primary: {
                      sx: {
                        fontSize: "14.5px",
                        fontWeight: isActivePath("/account") ? 500 : 400,
                      },
                    },
                  }}
                />
              )}
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding className={isMobile ? "my-0" : ""}>
            <ListItemButton
              onClick={() => handleNavigation("/profile")}
              className={`min-h-[44px] ${isActivePath("/profile") ? "bg-smoke" : ""} ${isDrawerOpen ? "px-6" : "justify-center px-2"}`}
            >
              <ListItemIcon
                className={`min-w-0 ${isActivePath("/profile") ? "text-gray-900" : "text-gray-600"} ${isDrawerOpen ? "mr-4" : "mr-0"}`}
              >
                <PersonOutline fontSize="small" />
              </ListItemIcon>
              {isDrawerOpen && (
                <ListItemText
                  primary="Profile"
                  className={isActivePath("/profile") ? "text-gray-900" : "text-gray-700"}
                  slotProps={{
                    primary: {
                      sx: {
                        fontSize: "14.5px",
                        fontWeight: isActivePath("/profile") ? 500 : 400,
                      },
                    },
                  }}
                />
              )}
            </ListItemButton>
          </ListItem>

          {canTakeTour && (
            <ListItem disablePadding className={isMobile ? "my-0" : ""}>
              <ListItemButton
                onClick={handleTakeTour}
                className={`min-h-[44px] ${isDrawerOpen ? "px-6" : "justify-center px-2"}`}
              >
                <ListItemIcon className={`min-w-0 text-gray-600 ${isDrawerOpen ? "mr-4" : "mr-0"}`}>
                  <ExploreOutlined fontSize="small" />
                </ListItemIcon>
                {isDrawerOpen && (
                  <ListItemText
                    primary={TOUR_COPY.menuTakeTour}
                    className="text-gray-700"
                    slotProps={{
                      primary: { sx: { fontSize: "14.5px", fontWeight: 400 } },
                    }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          )}

          <ListItem disablePadding className={isMobile ? "my-0" : ""}>
            <ListItemButton
              onClick={(e) => {
                e.stopPropagation();
                signOut({ redirectUrl: "/sign-in" });
              }}
              className={`min-h-[44px] ${isDrawerOpen ? "px-6" : "justify-center px-2"}`}
            >
              <ListItemIcon className={`min-w-0 text-gray-600 ${isDrawerOpen ? "mr-4" : "mr-0"}`}>
                <LogoutOutlined fontSize="small" />
              </ListItemIcon>
              {isDrawerOpen && (
                <ListItemText
                  primary="Log out"
                  className="text-gray-700"
                  slotProps={{ primary: { sx: { fontSize: "14.5px", fontWeight: 400 } } }}
                />
              )}
            </ListItemButton>
          </ListItem>
        </List>
      </Collapse>

      <Divider className="border-gray-200" />
    </>
  );
}
